import { useState } from "react";
import { Building2 } from "lucide-react";
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import EmailVerification from "@/components/EmailVerification/EmailVerification";
import { signInWithEmailLink } from "@/ajax/auth/signInWithEmailLink";
import { ProviderInviteShell } from "./provider-invite-shell";

/**
 * Signed-out invite state. The invite is bound to a known email, so we skip
 * the generic sign-in page: clicking Accept emails a login code straight to
 * that address and drops the invitee on the code-entry screen. Verifying the
 * code signs them in; the parent page then re-renders to the accept step (it
 * watches the Supabase session via useSupabaseSessionEmail).
 */
export function InvitationSignInCard({
  organizationName,
  businessUnitName,
  invitedEmail,
}: {
  organizationName: string;
  businessUnitName: string;
  invitedEmail: string;
}) {
  const [codeSent, setCodeSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCode = async () => {
    const { error } = await signInWithEmailLink({
      email: invitedEmail,
      shouldCreateUser: true,
    });
    if (error) {
      throw new Error(error.message);
    }
  };

  const handleAccept = async () => {
    setIsSending(true);
    setError(null);
    try {
      await sendCode();
      setCodeSent(true);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not send your code. Try again.",
      );
    } finally {
      setIsSending(false);
    }
  };

  if (codeSent) {
    return (
      <EmailVerification
        email={invitedEmail}
        onComplete={() => {
          // No-op: verifyOtp set the session; the parent page re-renders to
          // the accept step via its Supabase auth-state subscription.
        }}
        onBack={() => setCodeSent(false)}
        onResend={sendCode}
      />
    );
  }

  return (
    <ProviderInviteShell>
      <CardHeader className="items-center text-center">
        <div className="rounded-full bg-blue-100 p-3 mb-2">
          <Building2 className="h-6 w-6 text-blue-600" />
        </div>
        <CardTitle className="text-xl">You&apos;re invited</CardTitle>
        <CardDescription>
          {organizationName} invited you to join {businessUnitName}&apos;s
          provider roster on Saile.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-center text-sm text-gray-600">
          Invited email: <span className="font-medium">{invitedEmail}</span>
        </p>
        <Button className="w-full" onClick={handleAccept} disabled={isSending}>
          {isSending ? "Sending code…" : "Accept invitation"}
        </Button>
        {error && (
          <p className="text-center text-sm text-destructive">{error}</p>
        )}
        <p className="text-center text-xs text-gray-500">
          We&apos;ll email a login code to {invitedEmail} to confirm it&apos;s
          you.
        </p>
      </CardContent>
    </ProviderInviteShell>
  );
}
